import { Fragment, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { routes } from '@/constants/Constants';
import { TRANSUNION_REFERRAL_ID } from '@/features/Registration/hooks/useReferralId';
import { isLogoutInProgress } from '@/shared/api';
import Loader from '@/shared/components/Loader';
import MyLandbot from '@/shared/components/Landbot';
import { UserIdentification } from '@/shared/providers/analytics';
import { useCurrentUser, usePermission, UserPermissions } from '@/shared/session';
import { cachedSessionStorage } from '@/utils/cachedSessionStorage';
import { shouldUserStayInEnrollProperty } from '@/utils/commonUtils';
import type { User } from '@/utils/types/User';

const getEnrollFormRedirectTarget = (pathname: string) =>
  pathname.match(new RegExp('/enroll/property-(form|enroll)(#.*$|$)'))
    ? pathname
    : routes.propertyEnrollmentForm;

/** Temporary Onboarding policy. Move to the Onboarding module during its migration. */
export const shouldRedirectToFirstPropertyEnroll = (
  pathname: string,
  user: User | undefined,
  canEnrollProperties: boolean,
  hasFirstPropertyState: boolean,
) => {
  const shouldStayInEnrollment =
    canEnrollProperties && shouldUserStayInEnrollProperty(pathname, user);
  if (!shouldStayInEnrollment) {
    return false;
  }

  const isOnEnrollmentPage = getEnrollFormRedirectTarget(pathname) === pathname;
  return !(isOnEnrollmentPage && hasFirstPropertyState);
};

export function AuthenticatedBoundary() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading, error, status } = useCurrentUser();
  const { notSet, enabled } = usePermission(UserPermissions.Properties.CanEnrollProperty);
  const canEnrollProperties = notSet || enabled;

  const shouldRedirectToEnroll = shouldRedirectToFirstPropertyEnroll(
    location.pathname,
    user,
    canEnrollProperties,
    location.state?.isFirstProperty === true,
  );
  const needsRedirect = Boolean(user && (user.role !== 'landlord' || shouldRedirectToEnroll));
  const redirectEvaluated = status === 'error' || (status === 'success' && !needsRedirect);

  useEffect(() => {
    if (status !== 'success') {
      return;
    }

    if (user.role !== 'landlord') {
      navigate(routes.limitation, { replace: true });
      return;
    }

    if (shouldRedirectToEnroll) {
      cachedSessionStorage.setItem('isUserFirstTimeEnrolling', 'true');
      navigate(`${routes.propertyEnrollmentForm}${location.search}${location.hash}`, {
        replace: true,
        state: { isFirstProperty: true },
      });
    }
  }, [location.hash, location.search, navigate, shouldRedirectToEnroll, status, user]);

  if (error) {
    // An auth failure has already started a logout; hold the loader until its
    // redirect lands instead of flashing the error page.
    return isLogoutInProgress() ? <Loader /> : <Navigate to={routes.error500} replace={true} />;
  }

  return (
    <Fragment>
      {status === 'success' && user ? <UserIdentification user={user} /> : null}
      {isLoading || !redirectEvaluated ? (
        <Loader />
      ) : (
        <Fragment>
          {user &&
          canEnrollProperties &&
          user.partner?.toLowerCase() === TRANSUNION_REFERRAL_ID.toLowerCase() ? (
            <MyLandbot />
          ) : null}
          <Outlet />
        </Fragment>
      )}
    </Fragment>
  );
}
